<script setup>
import { useLocalStorageManager } from '@0x234/common-vue/storage/local-storage-manager';
import { LEGAL_DOCUMENTS_EFFECTIVE_DATE, LEGAL_UPDATE_NOTICE_DAYS } from 'src/configs/legal';
import AppRoute from 'src/router/app-route';
import { Route } from 'src/router/routes';
import { computed } from 'vue';

const MILLISECONDS_PER_DAY = 86_400_000;

const localStorageManager = useLocalStorageManager();

const dismissedUpdate = localStorageManager.storeRef('legal/dismissedUpdate', null);

// Read in the visitor's own time, so the date the notice gives is the date on the documents wherever they are.
const effectiveDate = new Date(`${LEGAL_DOCUMENTS_EFFECTIVE_DATE}T00:00:00`);
const hasTakenEffect = Date.now() >= effectiveDate.getTime();
const noticeEndsAt = effectiveDate.getTime() + (LEGAL_UPDATE_NOTICE_DAYS + 1) * MILLISECONDS_PER_DAY;
const isWithinNoticePeriod = Date.now() < noticeEndsAt;
const displayDate = effectiveDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
const privacySummaryRoute = AppRoute.build(Route.PrivacyPolicy, {}, { hash: '#tldr' });
const termsSummaryRoute = AppRoute.build(Route.TermsOfService, {}, { hash: '#tldr' });

const isShown = computed(() => isWithinNoticePeriod && dismissedUpdate.value !== LEGAL_DOCUMENTS_EFFECTIVE_DATE);

function dismiss() {
    dismissedUpdate.value = LEGAL_DOCUMENTS_EFFECTIVE_DATE;
}
</script>

<template>
    <div
        v-if="isShown"
        aria-label="Update to our legal documents"
        class="legal-update-notice notice-banner q-pa-md"
        role="region"
    >
        <div class="row items-start no-wrap q-gutter-x-sm">
            <p class="col text-body2 q-my-none">
                Our Privacy Policy and Terms of Service {{ hasTakenEffect ? 'changed' : 'change' }} on {{ displayDate }}
                to cover the market pages, My Market and the optional Career profile.
                <router-link :to="privacySummaryRoute">Privacy summary</router-link>
                ·
                <router-link :to="termsSummaryRoute">Terms summary</router-link>
            </p>
            <q-btn
                aria-label="Dismiss this notice"
                color="primary"
                flat
                icon="sym_r_close"
                padding="6px"
                title="Dismiss this notice"
                @click="dismiss"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
// Fixed to the foot of the window so it reaches every visitor without pushing any page's hero down. It sits over
// content, so the notice tint is laid over an opaque surface.
.legal-update-notice {
    background: linear-gradient(rgba($primary, 0.06), rgba($primary, 0.06)), $white;
    bottom: 16px;
    left: 16px;
    margin: 0 auto;
    max-width: 720px;
    position: fixed;
    right: 16px;
    z-index: 2000;

    .body--dark & {
        background: linear-gradient(rgba($primary, 0.12), rgba($primary, 0.12)), $dark;
    }
}
</style>
